import { Modal } from "@mui/material";
import React, { useState, useRef } from "react";
import { uploadFileBase64, uploadImageBase64 } from "../../../../../config/Services"; 

const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader?.result?.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const UploadModal = ({ open, onClose, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const validateFile = (file) => {
    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setError("Only PNG, JPEG, PDF, or CSV files are allowed");
      return false;
    }

    if (file.size > maxSize) {
      setError("File size must be less than 5MB");
      return false;
    }

    setError("");
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError("");

      // Convert file to base64
      const base64String = await convertFileToBase64(selectedFile);

      // Get file extension
      const fileExtension = selectedFile.name.split(".").pop();
      const fileName = `doc-${Date.now()}.${fileExtension}`;

      // Prepare payload
      const filePayload = {
        path: "finance/expense",
        uploadType: "EXPENSE_BILL",
        files: {
          [fileName]: base64String,
        },
      };

      // Upload file
      const res = await uploadFileBase64(filePayload);
      const data = res?.data?.data?.data;

      if (data) {
        const uploadedFileUrl = data[fileName] || Object.values(data)[0];
        onUpload({
          name: selectedFile.name,
          url: uploadedFileUrl,
          size: selectedFile.size,
          type: selectedFile.type,
        });

        handleCloseModal();
      } else {
        setError("Failed to upload file. No data returned.");
        setUploading(false);
      }
    } catch (err) {
      setError("Failed to upload file. Please try again.");
      setUploading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedFile(null);
    setError("");
    setDragActive(false);
    setUploading(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleCloseModal}>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "600px",
          maxWidth: "90%",
          backgroundColor: "#fff",
          border: "1px solid #E6E6E6",
          padding: "2rem",
          borderRadius: "0.75rem",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>Upload File</h3>
          <button
            onClick={handleCloseModal}
            disabled={uploading}
            style={{
              background: "none",
              border: "none",
              cursor: uploading ? "not-allowed" : "pointer",
              color: "#666",
            }}
          >
            ✕
          </button>
        </div>

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={!selectedFile && !uploading ? handleSelectFileClick : undefined}
          style={{
            border: dragActive ? "2px dashed #3b82f6" : "2px dashed #e5e7eb",
            borderRadius: "0.5rem",
            padding: "3rem 2rem",
            textAlign: "center",
            backgroundColor: dragActive ? "#eff6ff" : "#fafafa",
            marginBottom: "1.5rem",
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
          }}
        >
          {!selectedFile ? (
            <>
              <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                Drag and drop your file here, or click to select.
              </p>
              <p style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                Allowed formats: PNG, JPEG, PDF, CSV (max 5MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".png, .jpg, .jpeg, .pdf, .csv, .xls, .xlsx"
                onChange={handleFileChange}
                style={{ display: "none" }}
                disabled={uploading}
              />
            </>
          ) : (
            <div>
              <p style={{ color: "#059669", fontWeight: 500 }}>
                {uploading ? "Uploading..." : "File Selected"}
              </p>
              <p>{selectedFile.name}</p>
              <p style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
              </p>
              {!uploading && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setError("");
                  }}
                  style={{
                    marginTop: "0.75rem",
                    padding: "0.375rem 0.75rem",
                    color: "#ef4444",
                    border: "1px solid #ef4444",
                    borderRadius: "0.25rem",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          )}

          {error && <p style={{ color: "#dc2626", marginTop: "0.75rem" }}>{error}</p>}
        </div>

        <button
          onClick={selectedFile ? handleUpload : handleSelectFileClick}
          disabled={!selectedFile || uploading}
          style={{
            width: "100%",
            padding: "0.75rem",
            backgroundColor: selectedFile && !uploading ? "#3b82f6" : "#d1d5db",
            color: "#fff",
            border: "none",
            borderRadius: "0.375rem",
            fontSize: "0.875rem",
            cursor: selectedFile && !uploading ? "pointer" : "not-allowed",
          }}
        >
          {uploading ? "Uploading..." : selectedFile ? "Upload File" : "Select File"}
        </button>
      </div>
    </Modal>
  );
};

export default UploadModal;
